import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Settings, LogOut, Menu, X,
  ChartLine as LineChart, Table, Users, Building2,
  ClipboardList, UserCog, ShieldCheck, Package,
  LayoutList, CircleUser as UserCircle, GraduationCap, Brain, Zap,
} from 'lucide-react';
import { useState, useEffect } from 'react';
import { useTranslation }      from 'react-i18next';
import { useAuth }             from '@/features/auth';
import { useCompany }          from '@/context/CompanyContext';
import { usePermissions }      from '@/hooks/usePermissions';
import { supabase }            from '@/lib/supabase';
import type { PermissionRequest } from '@/lib/rbac';
import type { AppModule }         from '@/types/database';

interface NavItem {
  nameKey: string;
  path:    string;
  icon:    React.ReactNode;
  requires?: PermissionRequest;
}

// ─── Module home paths — navigated to immediately when tile is clicked ────────
const MODULE_HOME: Record<string, string> = {
  cashflow:  '/app/cashflow',
  hr:        '/app/employees/portfolio',
  inventory: '/app/inventory',
};

// ─── Nav items per module ─────────────────────────────────────────────────────

const CASHFLOW_ITEMS: NavItem[] = [
  { nameKey: 'nav.dashboard',       path: '/app/cashflow',          icon: <LayoutDashboard className="w-4 h-4" />, requires: { module: 'cashflow', level: 'viewer' } },
  { nameKey: 'nav.cashflowTables',  path: '/app/cashflow/tables',   icon: <Table           className="w-4 h-4" />, requires: { module: 'cashflow', level: 'viewer' } },
  { nameKey: 'nav.cashflowSettings',path: '/app/cashflow/settings', icon: <Settings        className="w-4 h-4" />, requires: { module: 'cashflow', level: 'editor' } },
];

const HR_ADMIN_ITEMS: NavItem[] = [
  { nameKey: 'nav.employeeSettings',    path: '/app/employees/settings',   icon: <Building2     className="w-4 h-4" />, requires: { module: 'hr', level: 'admin'  } },
  { nameKey: 'nav.employeePortfolio',   path: '/app/employees/portfolio',  icon: <Users         className="w-4 h-4" />, requires: { module: 'hr', level: 'viewer' } },
  { nameKey: 'nav.onboardingManagement',path: '/app/employees/onboarding', icon: <UserCog       className="w-4 h-4" />, requires: { module: 'hr', level: 'editor' } },
  { nameKey: 'nav.actionLog',           path: '/app/employees/action-log', icon: <ClipboardList className="w-4 h-4" />, requires: { module: 'hr', level: 'viewer' } },
];

const INVENTORY_ITEMS: NavItem[] = [
  { nameKey: 'nav.inventoryMovements', path: '/app/inventory/movements', icon: <LayoutList className="w-4 h-4" />, requires: { module: 'inventory', level: 'viewer' } },
  { nameKey: 'nav.inventoryStock',     path: '/app/inventory/stock',     icon: <Package    className="w-4 h-4" />, requires: { module: 'inventory', level: 'viewer' } },
  { nameKey: 'nav.inventorySettings',  path: '/app/inventory/settings',  icon: <Settings   className="w-4 h-4" />, requires: { module: 'inventory', level: 'editor' } },
];

// ─── Module display definitions ───────────────────────────────────────────────

interface ModuleDef {
  labelKey: string;
  icon:     React.ReactNode;
  tileColor: string;
  activeColor: string;
}

const MODULE_DEFS: Record<string, ModuleDef> = {
  cashflow:  { labelKey: 'nav.cashflow',  icon: <LineChart className="w-5 h-5" />, tileColor: 'bg-blue-50 text-blue-700 border-blue-200',    activeColor: 'bg-blue-50 text-blue-700 border-blue-200'    },
  hr:        { labelKey: 'nav.employees', icon: <Users     className="w-5 h-5" />, tileColor: 'bg-emerald-50 text-emerald-700 border-emerald-200', activeColor: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  inventory: { labelKey: 'nav.inventory', icon: <Package   className="w-5 h-5" />, tileColor: 'bg-amber-50 text-amber-700 border-amber-200',   activeColor: 'bg-amber-50 text-amber-700 border-amber-200'   },
};

// ─── Component ────────────────────────────────────────────────────────────────

export function Sidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { signOut, user }                                          = useAuth();
  const { t }                                                      = useTranslation();
  const { activeCompanyId, activeCompanyRole, activeModules, loadingCompanies } = useCompany();
  const { can, isPremium, isSuperAdmin }                           = usePermissions();

  // ── Which module's nav is currently shown — derived purely from URL ─────────
  const activeModule: AppModule | null = (() => {
    if (location.pathname.startsWith('/app/cashflow'))                        return 'cashflow';
    if (location.pathname.startsWith('/app/employees') ||
        location.pathname.startsWith('/app/hr'))                              return 'hr';
    if (location.pathname.startsWith('/app/inventory'))                       return 'inventory';
    return null;
  })();

  // ── Employee self-ID (member role only) ───────────────────────────────────
  const [myEmployeeId, setMyEmployeeId] = useState<string | null>(null);

  useEffect(() => {
    if (!user?.id || !activeCompanyId || activeCompanyRole !== 'member') {
      setMyEmployeeId(null);
      return;
    }
    let cancelled = false;
    supabase
      .from('hr_employees')
      .select('id')
      .eq('company_id', activeCompanyId)
      .eq('user_id', user.id)
      .limit(1)
      .single()
      .then(({ data }) => { if (!cancelled) setMyEmployeeId(data?.id ?? null); });
    return () => { cancelled = true; };
  }, [user?.id, activeCompanyId, activeCompanyRole]);

  const isAdminOrOwner = isSuperAdmin || activeCompanyRole === 'admin';

  const hrItems: NavItem[] = isAdminOrOwner
    ? HR_ADMIN_ITEMS
    : myEmployeeId
      ? [
          { nameKey: 'nav.myProfile',    path: `/app/employees/${myEmployeeId}?tab=personal`,   icon: <UserCircle    className="w-4 h-4" /> },
          { nameKey: 'nav.myOnboarding', path: `/app/employees/${myEmployeeId}?tab=onboarding`, icon: <GraduationCap className="w-4 h-4" /> },
        ]
      : [];

  const moduleNavItems: Record<string, NavItem[]> = {
    cashflow:  CASHFLOW_ITEMS,
    hr:        hrItems,
    inventory: INVENTORY_ITEMS,
  };

  const visibleItems = (items: NavItem[]) =>
    items.filter(item => !item.requires || can(item.requires));

  const stripQuery = (path: string) => path.split('?')[0] ?? '';
  const isActive   = (path: string) => {
    const clean = stripQuery(path);
    return clean !== '' && location.pathname === clean;
  };

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLoggingOut,     setIsLoggingOut]     = useState(false);

  const handleLogout = async () => {
    setIsLoggingOut(true);
    try { await signOut(); navigate('/'); }
    catch (e) { console.error(e); }
    finally { setIsLoggingOut(false); }
  };

  // ── Picker: shown when user is not inside any module yet ──────────────────
  const ModulePicker = () => (
    <div className="px-3 py-2 space-y-2">
      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider px-1 mb-3">
        {t('nav.modules', 'מודולים')}
      </p>
      {activeModules.map(mod => {
        const def = MODULE_DEFS[mod];
        if (!def) return null;
        return (
          <button
            key={mod}
            onClick={() => navigate(MODULE_HOME[mod] ?? '/app')}
            className={`
              w-full flex items-center gap-3 px-4 py-3 rounded-xl border text-sm font-medium
              transition-all duration-200 hover:opacity-80
              ${def.tileColor}
            `}
          >
            {def.icon}
            <span>{t(def.labelKey)}</span>
          </button>
        );
      })}
    </div>
  );

  // ── Module nav: full nav for the current module ───────────────────────────
  const ModuleNav = ({ mod }: { mod: AppModule }) => {
    const def   = MODULE_DEFS[mod];
    const items = visibleItems(moduleNavItems[mod] ?? []);
    if (!def) return null;

    return (
      <div>
        {/* Back to picker — only when the company has multiple modules */}
        {activeModules.length > 1 && (
          <button
            onClick={() => navigate('/app/dashboard')}
            className="flex items-center gap-2 px-5 py-2 mb-2 text-sm text-gray-500 hover:text-gray-700 transition-colors w-full text-right"
          >
            <span className="text-xs">‹</span>
            {t('nav.allModules', 'כל המודולים')}
          </button>
        )}

        {/* Module header — clicking navigates to the module home */}
        <Link
          to={MODULE_HOME[mod] ?? '/app'}
          onClick={() => setIsMobileMenuOpen(false)}
          className={`flex items-center gap-3 px-4 py-3 mx-3 mb-3 rounded-xl border transition-opacity hover:opacity-80 ${def.activeColor}`}
        >
          {def.icon}
          <span className="font-semibold text-sm">{t(def.labelKey)}</span>
        </Link>

        {/* Nav links */}
        <div className="px-3 space-y-0.5">
          {items.map(item => (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => setIsMobileMenuOpen(false)}
              className={`
                flex items-center gap-3 px-4 py-2.5 rounded-lg transition-all duration-150 text-sm
                ${isActive(item.path)
                  ? 'bg-primary-100 text-primary-700 font-medium'
                  : 'text-gray-700 hover:bg-gray-100'}
              `}
            >
              {item.icon}
              <span>{t(item.nameKey)}</span>
            </Link>
          ))}
        </div>
      </div>
    );
  };

  // ── Bottom nav — Skills + AI Reports + Settings + Admin ──────────────────
  const BottomNav = () => (
    <div className="px-3 space-y-0.5">
      <Link
        to="/app/skills"
        onClick={() => setIsMobileMenuOpen(false)}
        className={`
          flex items-center gap-3 px-4 py-2.5 rounded-lg transition-all duration-150 text-sm
          ${location.pathname.startsWith('/app/skills')
            ? 'bg-primary-100 text-primary-700 font-medium'
            : 'text-gray-700 hover:bg-gray-100'}
        `}
      >
        <Zap className="w-4 h-4" />
        <span>סקילים AI</span>
      </Link>

      <Link
        to="/app/reports/ai"
        onClick={() => setIsMobileMenuOpen(false)}
        className={`
          flex items-center gap-3 px-4 py-2.5 rounded-lg transition-all duration-150 text-sm
          ${location.pathname.startsWith('/app/reports/ai')
            ? 'bg-primary-100 text-primary-700 font-medium'
            : 'text-gray-700 hover:bg-gray-100'}
        `}
      >
        <Brain className="w-4 h-4" />
        <span>דוחות AI</span>
      </Link>

      <Link
        to="/app/settings"
        onClick={() => setIsMobileMenuOpen(false)}
        className={`
          flex items-center gap-3 px-4 py-2.5 rounded-lg transition-all duration-150 text-sm
          ${location.pathname.startsWith('/app/settings')
            ? 'bg-primary-100 text-primary-700 font-medium'
            : 'text-gray-700 hover:bg-gray-100'}
        `}
      >
        <Settings className="w-4 h-4" />
        <span>{t('nav.settings')}</span>
      </Link>

      {isSuperAdmin && (
        <Link
          to="/app/admin"
          onClick={() => setIsMobileMenuOpen(false)}
          className={`
            flex items-center gap-3 px-4 py-2.5 rounded-lg transition-all duration-150 text-sm
            ${isActive('/app/admin')
              ? 'bg-primary-100 text-primary-700 font-medium'
              : 'text-gray-700 hover:bg-gray-100'}
          `}
        >
          <ShieldCheck className="w-4 h-4" />
          <span>{t('nav.superAdmin', 'ניהול מערכת')}</span>
        </Link>
      )}
    </div>
  );

  // ── Main sidebar content ──────────────────────────────────────────────────
  const SidebarContent = () => {
    if (loadingCompanies) {
      return (
        <div className="flex-1 flex items-center justify-center">
          <div className="w-6 h-6 border-2 border-primary-300 border-t-primary-600 rounded-full animate-spin" />
        </div>
      );
    }

    // Which section to show in the module area:
    // 1. If currently inside a licensed module → show that module's full nav
    // 2. If company has multiple modules and we're not in any → show picker
    // 3. If company has exactly 1 module → always show that module's nav
    const moduleSection = (() => {
      if (activeModule && activeModules.includes(activeModule)) {
        return <ModuleNav mod={activeModule} />;
      }
      if (activeModules.length === 1) {
        return <ModuleNav mod={activeModules[0]!} />;
      }
      if (activeModules.length > 1) {
        return <ModulePicker />;
      }
      return null;
    })();

    return (
      <nav className="flex-1 overflow-y-auto py-4 flex flex-col gap-4">
        {moduleSection}

        {activeModules.length > 0 && (
          <div className="mx-4 border-t border-gray-100" />
        )}

        <BottomNav />
      </nav>
    );
  };

  return (
    <>
      <button
        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        className="lg:hidden fixed top-4 left-4 z-50 p-2 rounded-lg bg-white shadow-md"
        aria-label="Toggle menu"
      >
        {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
      </button>

      {isMobileMenuOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-30"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      <aside
        className={`
          fixed lg:static inset-y-0 left-0 z-40 lg:z-30
          w-64 bg-white border-r border-gray-200
          transform transition-transform duration-300 ease-in-out
          ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
      >
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-center h-16 border-b border-gray-100">
            <h1 className="text-2xl font-bold text-primary-600">Axelio</h1>
          </div>

          <SidebarContent />

          <div className="p-4 border-t border-gray-200 space-y-3">
            {(isPremium || isSuperAdmin) && (
              <div className={`
                px-3 py-1.5 rounded-full text-xs font-semibold text-center
                ${isSuperAdmin ? 'bg-blue-50 text-blue-700' : 'bg-amber-100 text-amber-700'}
              `}>
                {isSuperAdmin ? 'Super Admin' : '✨ Premium'}
              </div>
            )}

            {!isSuperAdmin && activeCompanyRole === 'member' && (
              <div className="px-3 py-1.5 rounded-full text-xs font-semibold text-center bg-gray-100 text-gray-600">
                עובד
              </div>
            )}

            {user?.email && (
              <div className="px-4 py-2 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500 mb-1">Signed in as</p>
                <p className="text-sm font-medium text-gray-900 truncate">{user.email}</p>
              </div>
            )}

            <button
              className="flex items-center gap-3 w-full px-4 py-3 text-red-600 hover:bg-red-50 rounded-lg transition-all duration-200 disabled:opacity-50"
              onClick={handleLogout}
              disabled={isLoggingOut}
            >
              <LogOut className={`w-5 h-5 ${isLoggingOut ? 'animate-spin' : ''}`} />
              <span>{isLoggingOut ? t('common.loading') : t('nav.logout')}</span>
            </button>
          </div>
        </div>
      </aside>
    </>
  );
}
